<template>
  <div
    v-loading.fullscreen.lock="isSubmitting"
    :element-loading-text="$t('Submitting...')"
    class="company-admin-form-page page-content">
    <el-row class="page-header -with-back-button" type="flex" justify="space-between">
      <el-col :span="24">
        <el-button type="text"
                   icon="el-icon-back"
                   class="back-button"
                   @click.prevent="backToCompanyAdminList">
        </el-button>
        <h2 class="page-title">{{ isUpdated ? 'แก้ไขบัญชีผู้ดูแลบริษัท' : 'เพิ่มบัญชีผู้ดูแลบริษัท' }}</h2>
      </el-col>
    </el-row>

    <el-form
      :model="adminForm"
      :rules="adminFormRules"
      ref="adminForm"
      label-width="120px"
      label-position="top"
      @submit.native.prevent="submitForm('adminForm')"
      :disabled="isSubmitting"
      class="admin-form">
      <el-row>
        <el-col :span="9">
          <el-form-item label="อีเมลผู้ดูแล" prop="email">
            <el-input
              v-model="adminForm.email"
              id="adminForm.email"
              :disabled="isViewerRole"
              placeholder="กรอกอีเมลผู้ดูแล"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="9">
          <el-form-item label="ชื่อผู้ดูแล" prop="name">
            <el-input
              v-model="adminForm.name"
              id="adminForm.name"
              :disabled="isViewerRole"
              placeholder="กรอกชื่อผู้ดูแล"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="9">
          <el-form-item label="หมายเลขโทรศัพท์" prop="tel">
            <el-input
              v-model="adminForm.tel"
              id="adminForm.tel"
              :disabled="isViewerRole"
              placeholder="กรอกหมายเลขโทรศัพท์"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="9">
          <el-form-item label="สถานะบัญชี" prop="status">
            <el-checkbox
              v-model="adminForm.status"
              :disabled="isViewerRole"
              class="status-holder"
              :class="{hide: isViewerRole}"
              id="adminForm.status">สถานะการใช้งาน (Flag = Enable, Not Flag = Disable/Blocked)</el-checkbox>
            <div class="user-status" v-if="isViewerRole">
              <el-tag
                :type="statusTypeFormatter(adminForm.status)"
                close-transition>{{statusFormatter(adminForm.status)}}</el-tag>
            </div>
            <div class="blocked-status-section" v-if="isBlocked && isViewerRole">
              <el-button
                plain
                type="primary"
                id="unblock-button"
                @click="unblockUser">ปลดล็อคบัญชีผู้ดูแลบริษัท</el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="18">
          <el-form-item label="รายละเอียดผู้ดูแล" prop="description">
            <el-input
              v-model="adminForm.description"
              :disabled="isViewerRole"
              id="adminForm.description"
              placeholder="กรอกรายละเอียดผู้ดูแล"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row v-if="shouldShowRole">
        <el-col :span="9">
          <el-form-item label="หน้าที่" prop="role">
            <el-radio-group v-model="adminForm.role" :disabled="isViewerRole">
              <el-radio
                label="ADMIN"
                id="adminForm.role_1">Admin</el-radio>
              <el-radio
                label="STAFF"
                id="adminForm.role_2">Staff</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="18">
          <el-form-item class="button-bar">
            <el-button
              type="primary"
              plain
              class="cancel-btn"
              @click="backToCompanyAdminList"
              id="adminForm.cancelButton">{{ 'Cancel' | translate }}</el-button>
            <div class="submit-button-group">
              <el-button
                type="danger"
                class="terminate-btn"
                icon="el-icon-delete"
                v-if="isUpdated && !isViewerRole"
                @click="confirmTerminate"
                id="adminForm.terminateButton">{{ 'Terminate' | translate }}</el-button>
              <el-button
                type="primary"
                v-if="!isViewerRole"
                id="adminForm.submitButton"
                native-type="submit">{{ isUpdated ? 'แก้ไขบัญชี' : 'สร้างบัญชี' }}</el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
  /* eslint no-param-reassign: 0 */
  import { createMask } from 'imask';
  import helpers from '@/libs/helpers';
  import _ from 'lodash';
  /**
   * Company Admin Form view
   */
  const CompanyAdminFormView = {
    name: 'company-admin-form',
    props: ['companyId', 'userId'],
    data() {
      return {
        isSubmitting: false,
        isSetupUpdateData: false,
        adminForm: {
          email: '',
          name: '',
          tel: '',
          status: true,
          description: '',
          role: 'ADMIN', // "ADMIN", "STAFF",
          assigned_profiles: [],
        },
        isBlocked: false,
        adminFormRules: {
          email: [
            { required: true, message: 'กรุณากรอกอีเมลผู้ดูแล', trigger: 'blur' },
            { type: 'email', message: this.$t('Please input a valid email address'), trigger: 'blur' },
          ],
          name: [
            { required: true, message: 'กรุณากรอกชื่อผู้ดูแล', trigger: 'blur' },
          ],
          tel: [
            { message: 'กรุณากรอกหมายเลขโทรศัพท์', trigger: 'blur' },
          ],
          /* description: [
            { required: true, message: 'กรุณากรอกรายละเอียดผู้ดูแล', trigger: 'blur' },
          ], */
        },
        mask: {
          tel: {
            mask: [
              { mask: '{02}-000-0000' },
              { mask: '{0}00-000-000' },
              { mask: '{0}00-000-0000' },
            ],
          },
        },
      };
    },
    computed: {
      isAuthenticated() {
        return this.$store.getters.idToken;
      },
      isViewerRole() {
        return this.$store.getters.isViewerRole;
      },
      isUpdated() {
        return typeof this.userId !== 'undefined';
      },
      userInfo() {
        if (this.isUpdated) {
          let userInfo = null;
          this.$store.getters.companyAdminList.forEach((item) => {
            if (item.id === this.userId) {
              userInfo = item;
            }
          });
          return userInfo;
          /* const indexId = `_${this.userId}`;
          if (typeof this.$store.getters.companyAdminListIndex[indexId] !== 'undefined') {
            const idx = this.$store.getters.companyAdminListIndex[indexId];
            return this.$store.getters.companyAdminList[idx];
          } */
        }
        return null;
      },
      shouldShowRole() {
        return false; // SCB ADMIN should not see this field
      },
    },
    methods: {
      backToCompanyAdminList() {
        this.$router.push({
          name: 'CompanyAdminList',
          params: {
            companyId: this.companyId,
          },
        });
      },
      confirmTerminate() {
        this.$confirm('ท่านกำลังจะลบบัญชีผู้ดูแลบริษัทนี้ออกจากระบบ ท่านต้องการยืนยันเพื่อดำเนินการต่อหรือไม่?', 'ระวัง', {
          confirmButtonText: this.$t('Confirm'),
          confirmButtonClass: 'danger-btn',
          cancelButtonText: this.$t('Cancel'),
          type: 'warning',
        }).then(() => {
          this.isSubmitting = true;
          this.$store.dispatch('DELETE_COMPANY_ADMIN_INFO', this.userId).then(() => {
            this.isSubmitting = false;
            this.$message({
              type: 'success',
              showClose: true,
              duration: 10000,
              message: 'ลบข้อมูลผู้ดูแลบริษัทสำเร็จแล้ว',
            });
            this.backToCompanyAdminList();
          }).catch((msg) => {
            this.isSubmitting = false;
            if (msg) {
              this.$store.dispatch('ADD_ERROR_FLASH', this.$t(msg));
            } else {
              this.$store.dispatch('ADD_ERROR_FLASH', this.$t('Unexpected error'));
            }
          });
        }).catch(() => {
        });
      },
      unblockUser() {
        this.$confirm('ท่านกำลังจะปลดล็อคบัญชีผู้ดูแลบริษัทนี้ ท่านต้องการยืนยันเพื่อดำเนินการต่อหรือไม่?', 'ระวัง', {
          confirmButtonText: this.$t('Confirm'),
          // confirmButtonClass: 'danger-btn',
          cancelButtonText: this.$t('Cancel'),
          type: 'warning',
        }).then(() => {
          this.isSubmitting = true;
          this.$store.dispatch('UNBLOCK_BILLER_USER', this.userId).then(() => {
            this.isSubmitting = false;
            this.$message({
              type: 'success',
              showClose: true,
              duration: 10000,
              message: 'ปลดล็อคบัญชีผู้ดูแลบริษัทสำเร็จแล้ว',
            });
            this.backToCompanyAdminList();
          }).catch((msg) => {
            this.isSubmitting = false;
            if (msg) {
              this.$store.dispatch('ADD_ERROR_FLASH', this.$t(msg));
            } else {
              this.$store.dispatch('ADD_ERROR_FLASH', this.$t('Unexpected error'));
            }
          });
        }).catch(() => {
        });
      },
      submitForm(formName) {
        this.$refs[formName].validate(this.validateForm);
      },
      validateForm(valid) {
        if (!valid) {
          this.$log.error('error submit!!');
          return false;
        }
        this.isSubmitting = true;
        const action = this.isUpdated ? 'UPDATE_COMPANY_ADMIN_INFO' : 'CREATE_COMPANY_ADMIN_INFO';
        const payload = JSON.parse(JSON.stringify(this.adminForm));
        // transform payload's status
        payload.status = payload.status ? 'active' : 'inactive';
        delete payload.created_at;
        delete payload.update_at;
        const reqPayload = {
          payload,
        };
        if (this.isUpdated) {
          reqPayload.userId = payload.id;
        } else {
          reqPayload.companyId = this.companyId;
        }
        this.$store.dispatch(action, reqPayload).then(() => {
          this.isSubmitting = false;
          let successMessage = 'สร้างบัญชีผู้ดูแลบริษัทใหม่สำเร็จแล้ว';
          if (this.isUpdated) {
            successMessage = 'แก้ไขข้อมูลบัญชีผู้ดูแลบริษัทสำเร็จแล้ว';
          }
          this.$message({
            type: 'success',
            showClose: true,
            duration: 10000,
            message: successMessage,
          });
          this.backToCompanyAdminList();
        }).catch((msg) => {
          this.isSubmitting = false;
          if (msg) {
            this.$store.dispatch('ADD_ERROR_FLASH', this.$t(msg));
          } else {
            this.$store.dispatch('ADD_ERROR_FLASH', this.$t('Unexpected error'));
          }
        });
        return true;
      },
      setupUpdateData() {
        if (typeof this.userInfo !== 'undefined' && this.userInfo) {
          this.isSetupUpdateData = true;
          Object.keys(this.userInfo).forEach((key) => {
            if (key === 'status') {
              const isActive = this.userInfo[key] === 'active';
              this.isBlocked = this.userInfo[key] === 'blocked';
              this.$set(this.adminForm, key, isActive);
            } else {
              this.$set(this.adminForm, key, this.userInfo[key]);
            }
          });
          // this.isBlocked = true; // -- For testing unblock
        }
      },
      clearUpdateData() {
        this.isSetupUpdateData = false;
        this.isBlocked = false;
        this.adminForm.email = '';
        this.adminForm.name = '';
        this.adminForm.tel = '';
        this.adminForm.status = true;
        this.adminForm.description = '';
        this.adminForm.role = 'ADMIN';
        this.adminForm.assigned_profiles.splice(0);
      },
      maskValue(form, field, maskOptions) {
        const mask = createMask(maskOptions);
        const val = form[field];
        if (val) {
          const resolvedValue = mask.resolve(val.toString());
          form[field] = resolvedValue;
        }
      },
      unmaskValue(form, field, maskOptions) {
        const mask = createMask(maskOptions);
        const val = form[field];
        if (val) {
          mask.resolve(val.toString());
          form[field] = mask.unmaskedValue;
        }
      },
      /* updateMaskTel() {
        try {
          this.maskValue(this.adminForm, 'tel', this.mask.tel);
        } catch (e) {
          console.error(e);
        }
      }, */
      /* eslint prefer-arrow-callback: 0 */
      handleInput: _.debounce(function handleInputFn(evt, refName, maskName) {
        helpers.handleInput(evt,
          this.$refs, refName,
          this.mask, maskName);
      }, process.env.HANDLE_INPUT_DEBOUNCE_INTERVAL, { tailing: true }),
      statusFormatter(status) {
        let statusText = 'Disabled';
        if (this.isBlocked) {
          statusText = 'Blocked';
        } else if (status) {
          statusText = 'Enabled';
        }
        return statusText;
      },
      statusTypeFormatter(status) {
        let type = 'info';
        if (this.isBlocked) {
          type = 'danger';
        } else if (status) {
          type = 'success';
        }
        return type;
      },
    },
    mounted() {
      // load data to form
      if (this.isUpdated) {
        setTimeout(() => {
          if (typeof this.userInfo === 'undefined' || this.userInfo === null) {
            this.backToCompanyAdminList();
          }
        }, 1000);
      }
      if (this.isUpdated && this.userInfo) {
        this.setupUpdateData();
      }
    },
    watch: {
      userInfo(newVal) {
        if (this.isUpdated && !this.isSetupUpdateData && newVal) {
          this.setupUpdateData();
        }
      },
    },
    beforeRouteEnter(to, from, next) {
      next(vm => vm.$store.dispatch('SELECT_CURRENT_COMPANY', vm.companyId));
    },
    beforeRouterLeave(to, from, next) {
      this.clearUpdateData();
      next();
    },
  };
  export default CompanyAdminFormView;
</script>

<style lang="scss">
  .admin-form {
    .cancel-btn, .terminate-btn {
      padding-top: 16px;
      padding-bottom: 16px;
    }
    .submit-button-group {
      float: right;
    }
    .status-holder {
      &.hide {
        display: none;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/company/company-admin-form.vue